import AccountService from '@/services/account';
import AiStatsService from '@/services/aiStats';
import AuthService from '@/services/auth';
import BillingService from '@/services/billing';
import ConfigService from '@/services/config';
import MenuService from '@/services/menu';
import PasskeyService from '@/services/passkey.ts';
import RoleService from '@/services/role';
import SupportService from '@/services/support';
import UserService from '@/services/user';
import axios, { type AxiosResponse, type GenericAbortSignal } from 'axios';

import i18n from '@/i18n';
import CompanyService from '@/services/company.ts';
import CustomerService from '@/services/customer.ts';
import CustomerGroupService from '@/services/customerGroup.ts';
import DashboardService from '@/services/dashboard.ts';
import InvitationService from '@/services/invitation.ts';
import InvoiceService from '@/services/invoice.ts';
import ProjectService from '@/services/project.ts';
import RecurringInvoiceService from '@/services/recurringInvoice.ts';
import SearchService from '@/services/search.ts';
import SubscriptionInvoiceService from '@/services/subscriptionInvoice.ts';
import TimeEntryService from '@/services/timeEntry.ts';
import TokenService from '@/services/token.ts';
import { useAuthStore } from '@/stores/auth.ts';
import { isPlanGateError, usePlanGateStore } from '@/stores/planGate.ts';
import { useToastStore } from '@/stores/toast.ts';
import '@/types/axios.d.ts';
import { isThrottleError, retryAfterMs } from '@/utils/apiError.ts';
import { readXsrf } from '@/utils/cookie.ts';
import { log } from '@/utils/log.ts';

// Request deduplication cache
const pendingRequests = new Map<string, Promise<any>>();

const api = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    withCredentials: true,
    headers: { 'X-Requested-With': 'XMLHttpRequest', 'Content-Type': 'application/json', Accept: 'application/json' },
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
});

/**
 * Clear all cookies by setting their expiration to the past
 * This ensures a clean logout state
 */
api.clearCookies = (): void => {
    document.cookie.split(';').forEach((cookie) => {
        const [name] = cookie.split('=').map((c) => c.trim());
        if (name) {
            // Clear cookie for current path
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

            // Clear cookie for root domain (if different)
            const domain = window.location.hostname;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;

            // Clear cookie for parent domain (for subdomains)
            const parentDomain = domain.split('.').slice(-2).join('.');
            if (parentDomain !== domain) {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${parentDomain};`;
            }
        }
    });

    log('All cookies cleared');
};

api.interceptors.request.use((cfg) => {
    const method = (cfg.method || 'get').toLowerCase();

    // Request deduplication for GET requests
    if (method === 'get') {
        const requestKey = `${cfg.url}${JSON.stringify(cfg.params || {})}`;
        if (pendingRequests.has(requestKey)) {
            cfg.signal = AbortSignal.timeout(0); // Cancel this request
            return Promise.reject({ __fromCache: true, key: requestKey });
        }
    }

    if (method !== 'get') {
        const hasHeader = !!(cfg.headers as any)?.['X-XSRF-TOKEN'] || !!api.defaults.headers.common['X-XSRF-TOKEN'];
        if (!hasHeader) {
            const token = readXsrf();
            if (token) {
                (cfg.headers as any)['X-XSRF-TOKEN'] = token;
            }
        }
    }

    // Add Accept-Language header based on current locale
    (cfg.headers as any)['Accept-Language'] = i18n.global.locale.value;

    return cfg;
});

// Response interceptor for request deduplication
api.interceptors.response.use(
    (response) => {
        const method = response.config.method?.toLowerCase();

        // Clear from pending requests on success
        if (method === 'get') {
            const requestKey = `${response.config.url}${JSON.stringify(response.config.params || {})}`;
            pendingRequests.delete(requestKey);
        }

        // Clear ALL cached GET requests after mutations to ensure fresh data
        if (method === 'post' || method === 'put' || method === 'delete' || method === 'patch') {
            pendingRequests.clear();
        }

        return response;
    },
    (error) => {
        // Handle deduplication
        if (error.__fromCache) {
            return pendingRequests.get(error.key);
        }

        // Clear from pending requests on error
        if (error.config?.method?.toLowerCase() === 'get') {
            const requestKey = `${error.config.url}${JSON.stringify(error.config.params || {})}`;
            pendingRequests.delete(requestKey);
        }

        return Promise.reject(error);
    },
);

// Extend the api instance with custom methods and properties
api.setErrorHandler = (httpCode: number, handler: (error: any) => void): void => {
    api.interceptors.response.use(
        (response) => response,
        (error) => {
            if (error.response?.status === httpCode) {
                handler(error);
            }
            return Promise.reject(error);
        },
    );
};

api.downloadFileToClient = (response: AxiosResponse, defaultFilename: string): void => {
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    const contentDisposition = response.headers['content-disposition'];

    let fileName = defaultFilename;

    // Handle filename extraction from Content-Disposition header
    if (contentDisposition) {
        // Try different formats:
        // 1. RFC 5987 format: filename*=UTF-8''example.pdf
        // 2. With quotes: filename="example.pdf"
        // 3. Without quotes: filename=example.pdf

        const fileNameMatchRFC5987 = contentDisposition.match(/filename\*=UTF-8''(.+?)(?:;|$)/i);
        const fileNameMatchQuoted = contentDisposition.match(/filename="([^"]+)"/i);
        const fileNameMatchUnquoted = contentDisposition.match(/filename=([^;]+)/i);

        if (fileNameMatchRFC5987 && fileNameMatchRFC5987[1]) {
            fileName = decodeURIComponent(fileNameMatchRFC5987[1].trim());
        } else if (fileNameMatchQuoted && fileNameMatchQuoted[1]) {
            fileName = fileNameMatchQuoted[1].trim();
        } else if (fileNameMatchUnquoted && fileNameMatchUnquoted[1]) {
            fileName = fileNameMatchUnquoted[1].trim();
        }
    }

    // Create a temporary link to trigger the download
    link.href = url;
    link.setAttribute('download', fileName);

    document.body.appendChild(link);

    // Trigger the download and clean up
    link.click();
    link.remove();
    // Revoke the object URL, otherwise it leaks for the document's lifetime.
    window.URL.revokeObjectURL(url);
};

api.auth = new AuthService(api);
api.account = new AccountService(api);
api.user = new UserService(api);
api.passkey = new PasskeyService(api);
api.menu = new MenuService(api);
api.roles = new RoleService(api);
api.config = new ConfigService(api);
api.company = new CompanyService(api);
api.customer = new CustomerService(api);
api.customerGroup = new CustomerGroupService(api);
api.invitation = new InvitationService(api);
api.invoice = new InvoiceService(api);
api.recurringInvoice = new RecurringInvoiceService(api);
api.project = new ProjectService(api);
api.timeEntry = new TimeEntryService(api);
api.token = new TokenService(api);
api.aiStats = new AiStatsService(api);
api.support = new SupportService(api);
api.search = new SearchService(api);
api.billing = new BillingService(api);
api.subscriptionInvoice = new SubscriptionInvoiceService(api);
api.dashboard = new DashboardService(api);

api.setErrorHandler(401, async (error: any) => {
    // Skip handling if this is a logout request to prevent infinite loop.
    // The error is already propagated to the caller via setErrorHandler's
    // own `return Promise.reject(error)` — no need to re-throw here.
    if (error.config?.skipAuthRedirect) {
        return;
    }

    try {
        const authStore = useAuthStore();
        // Pass true so signOut preserves the current route as a post-login redirect target
        await authStore.signOut(true);
    } catch (e) {
        log('Error during automatic sign-out on 401 response:', e);
    }
    // Do NOT re-throw here. This handler runs as a fire-and-forget promise
    // (setErrorHandler does not await it), so throwing would create an
    // unhandled promise rejection. The error is already propagated to the
    // caller via setErrorHandler's `return Promise.reject(error)`.
});

// Plan-gating 403s (MILO-280). Only the machine-readable ones — a flat body
// with a recognised `code` — open the upgrade modal; generic 403s carry no code
// and fall through to the caller's own handling. The error is still rejected by
// setErrorHandler, so callers can also react if they want to.
//
// `skipPlanGate` opts a request out: the public invite-accept page (MILO-178)
// can trip a `limit_reached` 403 for an account the visitor doesn't belong to,
// where an upgrade CTA would be meaningless — that page renders its own message.
api.setErrorHandler(403, (error: any) => {
    if (error.config?.skipPlanGate) {
        return;
    }

    const data = error.response?.data;
    if (isPlanGateError(data)) {
        usePlanGateStore().trigger(data);
    }
});

// Rate limiting (milo-api 1.45.0): 120 requests a minute per user on the whole
// `api` group, on top of the throttles single routes carry. Only milo-api's own
// refusals are handled here (isThrottleError); the sign-in and public pages deal
// with the limits on their web routes themselves.
//
// A GET whose Retry-After is short is sent once more after that wait, so a burst
// at the end of a window passes unnoticed. Anything else is rejected as before,
// after a notice that tells the user when they can carry on. `skipRateLimitNotice`
// opts a request out of that notice, for callers with a message of their own.
const RATE_LIMIT_RETRY_MAX_WAIT_MS = 10_000;
// Retry-After counts whole seconds, so "0" can still be refused within that second.
const RATE_LIMIT_RETRY_MIN_WAIT_MS = 1_000;
// The caller's own error toast for the same refusal follows within this window.
const RATE_LIMIT_ERROR_HOLD_MS = 2_000;
// The notice stays up while the limiter refuses, within these bounds.
const RATE_LIMIT_NOTICE_MIN_MS = 5_000;
const RATE_LIMIT_NOTICE_MAX_MS = 60_000;

let rateLimitNoticeId: number | null = null;

const waitBeforeRetry = (ms: number, signal?: GenericAbortSignal): Promise<void> => {
    // An aborted request (a superseded search, say) must not sit out the wait:
    // the retry then fails straight away with axios' own CanceledError.
    if (signal?.aborted) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        const done = (): void => {
            clearTimeout(timer);
            signal?.removeEventListener?.('abort', done);
            resolve();
        };
        const timer = setTimeout(done, ms);
        signal?.addEventListener?.('abort', done);
    });
};

const notifyRateLimited = (waitMs: number | null): void => {
    const toast = useToastStore();
    toast.holdBackErrors(RATE_LIMIT_ERROR_HOLD_MS);

    // Requests refused together share one notice.
    if (rateLimitNoticeId !== null && toast.items.some((item) => item.id === rateLimitNoticeId)) {
        return;
    }

    const seconds = waitMs === null ? 0 : Math.ceil(waitMs / 1000);
    rateLimitNoticeId = toast.push({
        type: 'warning',
        message:
            seconds > 0
                ? i18n.global.t('error.tooManyRequestsRetryIn', seconds)
                : i18n.global.t('error.tooManyRequests'),
        timeout: Math.min(Math.max(waitMs ?? 0, RATE_LIMIT_NOTICE_MIN_MS), RATE_LIMIT_NOTICE_MAX_MS),
    });
};

api.interceptors.response.use(undefined, async (error: any) => {
    const config = error?.config;
    if (!config || !isThrottleError(error)) {
        return Promise.reject(error);
    }

    const waitMs = retryAfterMs(error);
    const isRead = (config.method ?? 'get').toLowerCase() === 'get';

    if (isRead && !config.rateLimitRetried && waitMs !== null && waitMs <= RATE_LIMIT_RETRY_MAX_WAIT_MS) {
        await waitBeforeRetry(Math.max(waitMs, RATE_LIMIT_RETRY_MIN_WAIT_MS), config.signal);
        return api.request({ ...config, rateLimitRetried: true });
    }

    if (!config.skipRateLimitNotice) {
        notifyRateLimited(waitMs);
    }

    return Promise.reject(error);
});

export default api;
